/**
 * Payments a tenant says they have made.
 *
 * The public payment page lets somebody who has done a bank transfer say so. Until now those
 * declarations went into the database and nowhere else — the endpoints existed and no screen
 * showed them, which is the worst of both worlds: the tenant is told the agency has been
 * notified, and the agency never sees it.
 *
 * **A declaration is a message, not money.** Nothing here changes a balance until somebody
 * confirms it, and the amount confirmed is the one typed on this screen — not the one the payer
 * claimed. A tenant who says they sent the full rent and sent half must not move the balance by
 * what they said. That is why the confirm control is an amount field and not a button.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { parseMoney } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Spinner,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";

interface Claim {
  claim: {
    id: string;
    amount: number;
    status: string;
    reference: string | null;
    paidOn: string | null;
    note: string | null;
    createdAt: string;
    payerName: string | null;
  };
  invoiceNumber: string;
  invoiceTotal: number;
  invoicePaid: number;
  invoiceDue: string;
  partyId: string;
  partyName: string;
  unitLabel: string | null;
  accountLabel: string | null;
}

const TONE: Record<string, "warn" | "success" | "danger" | "muted"> = {
  pending: "warn",
  confirmed: "success",
  rejected: "danger",
};

export function DeclaredPaymentsPage() {
  const { can } = useSession();
  const fmt = useFormat();
  const [status, setStatus] = useState("pending");
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["claims", status],
    queryFn: () => api.get<{ claims: Claim[]; today: string }>("/claims", { status }),
  });

  const confirm = useApiMutation({
    mutationFn: ({ id, amount, receivedOn }: { id: string; amount: string; receivedOn: string }) =>
      // The endpoint takes minor units. Posting the typed figure would record ₦60 for ₦6,000.
      api.post(`/claims/${id}/confirm`, { amount: parseMoney(amount) ?? 0, receivedOn }),
    // A confirmed claim writes a real payment, so everything downstream of a payment moves.
    invalidates: [["claims"], ["payments"], ["invoices"], ["dashboard"]],
    onSuccess: () => setOpen(null),
  });

  const reject = useApiMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/claims/${id}/reject`, { reason }),
    invalidates: [["claims"]],
    onSuccess: () => setOpen(null),
  });

  const rows = data?.claims ?? [];
  const tabs = [
    { to: "#pending", label: "Awaiting review" },
    { to: "#confirmed", label: "Confirmed" },
    { to: "#rejected", label: "Rejected" },
  ];

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Declared payments"
        back={{ to: "/app/payments", label: "Payments" }}
        subtitle="Tenants who say they have transferred the money. Nothing moves a balance until you confirm it."
        description={"Transfers tenants say they have made, waiting for you to check them against the bank. Confirm one and it becomes a real payment; reject it and nothing moves."}
      />

      {/* Not `Tabs`, because these filter in place rather than navigating — and a hash in the URL
          would fight the page transition. */}
      <div className="mb-4 flex gap-1 border-b border-ink-200">
        {tabs.map((tab) => {
          const value = tab.to.slice(1);
          return (
            <button
              key={value}
              type="button"
              onClick={() => setStatus(value)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                status === value
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-ink-500 hover:text-ink-800"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            title={status === "pending" ? "Nothing waiting for you." : `No ${status} declarations.`}
            hint="When a tenant taps “I have made this payment” on their payment link, it appears here."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const outstanding = row.invoiceTotal - row.invoicePaid;
            const overstated = row.claim.amount > outstanding;
            return (
              <Card key={row.claim.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-base font-semibold">
                      {fmt.money(row.claim.amount)}
                      <Badge tone={TONE[row.claim.status] ?? "muted"}>{row.claim.status}</Badge>
                    </p>
                    <p className="mt-0.5 text-sm text-ink-600">
                      <Link to={`/app/clients/${row.partyId}`} className="link">
                        {row.partyName}
                      </Link>{" "}
                      · <Link to={`/app/invoices`} className="link">{row.invoiceNumber}</Link>
                      {row.unitLabel ? ` · ${row.unitLabel}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-ink-500">
                      Declared {fmt.date(row.claim.createdAt.slice(0, 10))}
                      {row.claim.paidOn ? ` · said to be sent ${fmt.date(row.claim.paidOn)}` : ""}
                      {row.claim.reference ? ` · ref ${row.claim.reference}` : ""}
                      {row.accountLabel ? ` · into ${row.accountLabel}` : ""}
                    </p>
                    {row.claim.note ? <p className="mt-1.5 text-sm text-ink-600">“{row.claim.note}”</p> : null}
                  </div>

                  <div className="text-right text-sm">
                    <p className="eyebrow">Outstanding</p>
                    <p className="tabular font-semibold">{fmt.money(outstanding)}</p>
                  </div>
                </div>

                {/* The one thing a manager must not miss: they said more than the invoice is owed. */}
                {overstated && row.claim.status === "pending" ? (
                  <div className="mt-3">
                    <Alert tone="warn">
                      This is more than the {fmt.money(outstanding)} outstanding. Confirming it will leave a credit on
                      the account.
                    </Alert>
                  </div>
                ) : null}

                {row.claim.status === "pending" && can("payment.record") ? (
                  open === row.claim.id ? (
                    <form
                      className="mt-4 border-t border-ink-200 pt-4"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const form = new FormData(event.currentTarget);
                        const action = String(form.get("action"));
                        if (action === "reject") {
                          reject.mutate({ id: row.claim.id, reason: String(form.get("reason") ?? "") });
                        } else {
                          confirm.mutate({
                            id: row.claim.id,
                            amount: String(form.get("amount") ?? ""),
                            receivedOn: String(form.get("receivedOn") ?? ""),
                          });
                        }
                      }}
                    >
                      {confirm.errorMessage ? <Alert tone="danger">{confirm.errorMessage}</Alert> : null}
                      {reject.errorMessage ? <Alert tone="danger">{reject.errorMessage}</Alert> : null}

                      <div className="grid gap-3 sm:grid-cols-2">
                        <Field
                          label="Amount actually received"
                          htmlFor={`amount-${row.claim.id}`}
                          hint="Change it if the bank shows something different. This is what moves the balance."
                        >
                          <MoneyInput
                            id={`amount-${row.claim.id}`}
                            name="amount"
                            defaultValue={String(row.claim.amount / 100)}
                          />
                        </Field>
                        <Field label="Received on" htmlFor={`on-${row.claim.id}`}>
                          <Input
                            id={`on-${row.claim.id}`}
                            name="receivedOn"
                            type="date"
                            defaultValue={row.claim.paidOn ?? data?.today ?? ""}
                          />
                        </Field>
                      </div>

                      <Field label="Reason, if you are rejecting it" htmlFor={`why-${row.claim.id}`}>
                        <Input id={`why-${row.claim.id}`} name="reason" placeholder="Nothing has arrived" />
                      </Field>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button type="submit" name="action" value="confirm" disabled={confirm.isPending}>
                          {confirm.isPending ? "Recording…" : "Confirm and record the payment"}
                        </Button>
                        <Button type="submit" name="action" value="reject" variant="danger" disabled={reject.isPending}>
                          Reject
                        </Button>
                        <Button variant="ghost" onClick={() => setOpen(null)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="mt-3 flex gap-2">
                      <Button variant="secondary" onClick={() => setOpen(row.claim.id)}>
                        Review
                      </Button>
                    </div>
                  )
                ) : null}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
